import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { TrendingUp } from "lucide-react";

const MODULES = [
  { key: "all", label: "All" },
  { key: "dictation", label: "Dictation" },
  { key: "legal", label: "Legal" },
  { key: "pitman", label: "Pitman" },
];

export default function ProgressChart() {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("daily"); // "daily" | "attempt"
  const [moduleFilter, setModuleFilter] = useState("all");

  useEffect(() => {
    api.get("/attempts")
      .then((r) => setAttempts(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (moduleFilter === "all" ? attempts : attempts.filter((a) => a.module === moduleFilter)),
    [attempts, moduleFilter]
  );

  const moduleCounts = useMemo(() => {
    const c = { all: attempts.length, dictation: 0, legal: 0, pitman: 0 };
    for (const a of attempts) {
      if (c[a.module] !== undefined) c[a.module] += 1;
    }
    return c;
  }, [attempts]);

  const chartData = useMemo(() => {
    if (!filtered.length) return [];
    const sorted = [...filtered].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    if (mode === "attempt") {
      return sorted.slice(-30).map((a, i) => ({
        label: `#${i + 1}`,
        date: new Date(a.created_at).toLocaleDateString(),
        accuracy: a.accuracy_percent,
        wpm: a.wpm,
      }));
    }

    const groups = {};
    for (const a of sorted) {
      const day = new Date(a.created_at).toISOString().slice(0, 10);
      if (!groups[day]) groups[day] = { day, _acc: 0, _wpm: 0, n: 0 };
      groups[day]._acc += a.accuracy_percent;
      groups[day]._wpm += a.wpm;
      groups[day].n += 1;
    }
    return Object.values(groups)
      .sort((a, b) => a.day.localeCompare(b.day))
      .map((g) => ({
        label: new Date(g.day).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        date: g.day,
        accuracy: Math.round((g._acc / g.n) * 100) / 100,
        wpm: Math.round((g._wpm / g.n) * 100) / 100,
        attempts: g.n,
      }));
  }, [filtered, mode]);

  return (
    <div data-testid="progress-chart">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-2 flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5" /> Progress trend
          </div>
          <h3 className="font-display text-2xl sm:text-3xl font-black tracking-tight">Accuracy &amp; WPM over time</h3>
        </div>
        <div className="inline-flex border border-border rounded-lg overflow-hidden" role="tablist">
          <button
            onClick={() => setMode("daily")}
            data-testid="chart-toggle-daily"
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold transition-colors ${
              mode === "daily" ? "bg-brand-gold text-background" : "bg-transparent text-muted-foreground hover:bg-secondary"
            }`}
          >
            Daily
          </button>
          <button
            onClick={() => setMode("attempt")}
            data-testid="chart-toggle-attempt"
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold border-l border-border transition-colors ${
              mode === "attempt" ? "bg-brand-gold text-background" : "bg-transparent text-muted-foreground hover:bg-secondary"
            }`}
          >
            Per attempt
          </button>
        </div>
      </div>

      {/* Module filter pills */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="chart-module-filter" role="tablist">
        {MODULES.map((m) => {
          const active = moduleFilter === m.key;
          const count = moduleCounts[m.key] ?? 0;
          return (
            <button
              key={m.key}
              onClick={() => setModuleFilter(m.key)}
              data-testid={`chart-module-${m.key}`}
              aria-pressed={active}
              className={`group inline-flex items-center gap-2 px-4 py-2 border rounded-lg text-[10px] uppercase tracking-[0.2em] font-bold transition-all ${
                active
                  ? "bg-brand-blue text-white border-brand-blue"
                  : "bg-transparent text-muted-foreground border-border hover:border-brand-gold/50 hover:text-foreground"
              }`}
            >
              {m.label}
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                active ? "bg-white/20 text-white" : "bg-secondary text-muted-foreground"
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="h-72 flex items-center justify-center font-mono text-sm text-muted-foreground">Loading…</div>
      ) : chartData.length === 0 ? (
        <div className="h-72 flex items-center justify-center border border-dashed border-border rounded-2xl font-mono text-sm text-muted-foreground text-center px-6" data-testid="progress-chart-empty">
          {attempts.length === 0
            ? "No attempts yet — start practising to see your trend line."
            : `No attempts in the ${moduleFilter} module yet — try one to see progress here.`}
        </div>
      ) : (
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(148,163,184,0.15)" strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                stroke="hsl(var(--muted-foreground))"
                tick={{ fontFamily: "JetBrains Mono, monospace", fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                axisLine={{ stroke: "hsl(var(--border))" }}
              />
              <YAxis
                yAxisId="acc"
                orientation="left"
                stroke="var(--brand-emerald)"
                domain={[0, 100]}
                tick={{ fontFamily: "JetBrains Mono, monospace", fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                label={{ value: "Accuracy %", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "var(--brand-emerald)", fontFamily: "JetBrains Mono, monospace" } }}
              />
              <YAxis
                yAxisId="wpm"
                orientation="right"
                stroke="var(--brand-gold)"
                tick={{ fontFamily: "JetBrains Mono, monospace", fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                label={{ value: "WPM", angle: 90, position: "insideRight", style: { fontSize: 11, fill: "var(--brand-gold)", fontFamily: "JetBrains Mono, monospace" } }}
              />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  color: "hsl(var(--foreground))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 12,
                  fontFamily: "JetBrains Mono, monospace",
                  fontSize: 12,
                }}
                labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 700 }}
                itemStyle={{ color: "hsl(var(--foreground))" }}
              />
              <Legend
                wrapperStyle={{ fontFamily: "JetBrains Mono, monospace", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.2em", color: "hsl(var(--muted-foreground))" }}
              />
              <Line
                yAxisId="acc"
                type="monotone"
                dataKey="accuracy"
                name="Accuracy %"
                stroke="var(--brand-emerald)"
                strokeWidth={2.5}
                dot={{ r: 3, fill: "var(--brand-emerald)" }}
                activeDot={{ r: 5 }}
              />
              <Line
                yAxisId="wpm"
                type="monotone"
                dataKey="wpm"
                name="WPM"
                stroke="var(--brand-gold)"
                strokeWidth={2.5}
                dot={{ r: 3, fill: "var(--brand-gold)" }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
